/**
 * @file Reads `parameters/analytics.calendar` for the Overview: the calendar's
 * time zone, the `periodStart` of the fiscal year holding `to` (the answered
 * period's end, the server's own "now") and a calendar error. Consumer:
 * `useAnalyticsGoalProgress`, whose callers build goal ids from `periodStart`. While the
 * document loads or no answer is in, `periodStart` is null with no error; a
 * read error passes its message, and a missing calendar or a zone that cannot
 * place `to` reads "The analytics calendar is not set.".
 */
import { useEffect } from 'react';
import { useSnapshot } from 'valtio';

import type { AnalyticsCalendar } from '#types';

import { analyticsFiscalStartYear } from './analyticsFiscalStartYear';
import { analyticsFiscalYear } from './analyticsFiscalYear';
import { analyticsParametersStore } from './analyticsParametersStore';

const CALENDAR_NOT_SET = 'The analytics calendar is not set.';

export const useAnalyticsFiscalCalendar = (
  to: string | undefined,
): {
  timeZone: string | undefined;
  periodStart: string | null;
  calendarError: string | null;
} => {
  useEffect(() => {
    void analyticsParametersStore.connect();
    return () => analyticsParametersStore.disconnect();
  }, []);
  const parameters = useSnapshot(analyticsParametersStore.state);

  const calendar: Partial<AnalyticsCalendar> | undefined =
    parameters.data?.calendar;
  const timeZone = calendar?.timeZone;
  const fiscalYearStartMonth = calendar?.fiscalYearStartMonth;
  const startYear =
    timeZone && fiscalYearStartMonth && to !== undefined
      ? analyticsFiscalStartYear(
          { timeZone, fiscalYearStartMonth },
          new Date(to),
        )
      : null;
  const periodStart =
    fiscalYearStartMonth && startYear !== null
      ? analyticsFiscalYear(fiscalYearStartMonth, startYear).periodStart
      : null;
  const isUnset =
    !timeZone ||
    !fiscalYearStartMonth ||
    (to !== undefined && periodStart === null);
  const calendarError =
    parameters.error?.message ??
    (!parameters.loading && isUnset ? CALENDAR_NOT_SET : null);
  return { timeZone, periodStart, calendarError };
};
